import { posthog } from 'posthog-js';
import { assignmentFromCookies, assignmentProperties, parseAssignment, type LandingAssignment } from './landingExperiment';

const KEY = 'homepoint:landing-account-first-v1:signup';
const WEEK = 7 * 86400000;
type Account = { userId: string; createdAt: number; assignment: LandingAssignment; activated?: boolean };
function readAccount(): Account | null {
  try {
    const a = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (!a || typeof a.userId !== 'string' || !Number.isFinite(a.createdAt) || a.createdAt > Date.now()) return null;
    const assignment = parseAssignment(JSON.stringify(a.assignment));
    if (!assignment || Date.now() - a.createdAt > WEEK) return null;
    return { ...a, assignment };
  } catch { return null; }
}
/** Called only after the users API confirms account creation, on both signup paths. */
export function trackLandingAccountCreated(userId: string): void {
  try {
    const assignment = assignmentFromCookies(document.cookie);
    if (!assignment) return;
    const previous = readAccount();
    if (previous?.userId === userId) return;
    const account: Account = { userId, createdAt: Date.now(), assignment };
    localStorage.setItem(KEY, JSON.stringify(account));
    posthog.capture('landing_account_created', {
      ...assignmentProperties(assignment), account_id: userId,
      $insert_id: `landing-account-created-${userId}`,
    });
  } catch { /* Analytics must never interrupt account creation. */ }
}
/** Tag the new account after login so its subsequent events can be segmented. */
export function identifyLandingAccount(userId: string): void {
  try {
    const account = readAccount();
    if (account?.userId !== userId) return;
    posthog.setPersonProperties({}, {
      ...assignmentProperties(account.assignment),
      landing_account_created_at: new Date(account.createdAt).toISOString(),
    });
  } catch { /* Analytics is optional. */ }
}
/** Successful API actions only; clicks and failed requests never count as activation. */
export function trackLandingActivation(action: 'tour_requested' | 'buyer_home_added' | 'seller_listing_started'): void {
  try {
    const account = readAccount();
    if (!account || account.activated || !posthog._isIdentified() || posthog.get_distinct_id() !== account.userId) return;
    posthog.capture('landing_account_activated', {
      ...assignmentProperties(account.assignment), account_id: account.userId, action,
      hours_since_signup: (Date.now() - account.createdAt) / 3600000,
      $insert_id: `landing-account-activated-${account.userId}`,
    });
    localStorage.setItem(KEY, JSON.stringify({ ...account, activated: true }));
  } catch { /* Analytics must never interrupt the completed action. */ }
}

/** Count a successful tour request even when signup predates this experiment. */
export function trackLandingTourRequested(requestId: string): void {
  try {
    const assignment = assignmentFromCookies(document.cookie);
    if (!assignment || assignment.audience !== 'buyer' || !requestId) return;
    posthog.capture('landing_tour_requested', {
      ...assignmentProperties(assignment), event_id: requestId, request_source: 'app',
      $insert_id: `landing-tour-app-${requestId}`,
    });
  } catch { /* Analytics must never interrupt a successful request. */ }
}
